import React, { createContext, KeyboardEvent, useContext, useMemo } from "react";
import { CircularProgress, MenuItem, SelectChangeEvent } from "@mui/material";
import { PifMenuSelect } from "../../pages/supervision/PostInterviewForm/pifSelectMenu";
import { PifListItem } from "../../pages/supervision/PostInterviewForm/types";
import { PostIfLookupItem } from "../../types/pifReferenceDto";

/** The Select's value for "no coordinator" - MUI needs a string, and null clears the assignment. */
const UNASSIGNED_VALUE = "";

export interface PostIFCoordinatorContextValue {
  coordinators: PostIfLookupItem[];
  /** HR with Edit on the PostIF-List module - the same rule the API enforces (CanAct 'mark'). */
  canAssign: boolean;
  /** Row ids whose save is in flight. */
  savingIds: ReadonlySet<string>;
  onAssign: (row: PifListItem, coordinatorUserId: number | null) => void;
}

/**
 * Why a context and not cell props: PostIFTable's columns are captured ONCE by useResizableColumns,
 * so a renderCell closing over the coordinator list or the saving set would keep its first-render
 * values forever. Read through context, the cell re-renders whenever they change.
 */
export const PostIFCoordinatorContext = createContext<PostIFCoordinatorContextValue>({
  coordinators: [],
  canAssign: false,
  savingIds: new Set(),
  onAssign: () => undefined,
});

interface PostIFCoordinatorCellProps {
  row: PifListItem;
}

/**
 * Epic 82838 - the HR list's HR Coordinator cell: an inline dropdown that assigns (or clears) the
 * coordinator, with a spinner while the save is in flight. Without permission it is read-only text,
 * so the cell still says who has the form. Blank means unassigned.
 */
const PostIFCoordinatorCell: React.FC<PostIFCoordinatorCellProps> = ({ row }) => {
  const { coordinators, canAssign, savingIds, onAssign } = useContext(PostIFCoordinatorContext);
  const saving = savingIds.has(row.id);

  // Keep the current assignee selectable even if they have since left the HR role, so the Select
  // never holds a value it has no option for (MUI warns and renders it blank).
  const options = useMemo(() => {
    const assigned = row.hrCoordinatorUserId;
    if (assigned === undefined || coordinators.some((option) => option.id === assigned)) return coordinators;
    return [...coordinators, { id: assigned, text: row.hrCoordinatorName ?? `User ${assigned}` }];
  }, [coordinators, row.hrCoordinatorUserId, row.hrCoordinatorName]);

  if (!canAssign) {
    return <span data-testid={`postif-coordinator-readonly-${row.id}`}>{row.hrCoordinatorName ?? ""}</span>;
  }

  const handleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onAssign(row, value === UNASSIGNED_VALUE ? null : Number(value));
  };

  // DataGrid treats Space/arrow keys in a cell as grid navigation; keep them for the Select.
  const stopGridKeys = (event: KeyboardEvent) => event.stopPropagation();

  return (
    <span className="postif-grid__coordinator">
      <PifMenuSelect<string>
        size="small"
        displayEmpty
        value={row.hrCoordinatorUserId === undefined ? UNASSIGNED_VALUE : String(row.hrCoordinatorUserId)}
        onChange={handleChange}
        onKeyDown={stopGridKeys}
        disabled={saving}
        inputProps={{ "aria-label": `HR Coordinator for ${row.title}` }}
        data-testid={`postif-coordinator-${row.id}`}
        className="postif-grid__coordinator-select"
      >
        <MenuItem value={UNASSIGNED_VALUE}>
          <span className="postif-grid__muted">Unassigned</span>
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option.id} value={String(option.id)}>
            {option.text ?? `User ${option.id}`}
          </MenuItem>
        ))}
      </PifMenuSelect>
      {saving && <CircularProgress size={14} aria-label="Saving" data-testid={`postif-coordinator-saving-${row.id}`} />}
    </span>
  );
};

export default PostIFCoordinatorCell;
